import { generateClient, GraphQLResult } from "aws-amplify/api";
// @ts-ignore
import { getProject } from "../graphql/queries";
import { buildFormGroupForFormGroupList, getFormGroupsByProjectId } from "../db/queries/formGroupQueries";
import { getProjectById } from "../db/queries/projectQueries";
import { recordSentry } from "utils/recordSentry";
import { FormGroup, FormTemplate, Project } from "db/definitions";

const getProjectFromIndexedDb = async (id: string, online: boolean): Promise<Project | null> => {
	// This is getting stuff from the indexedDb. We need to get all of the project data with the nested stuff
	// Step 2 and 3: Get project + formGroups by projectId
	// Step 4: Await promises
	const [project, formGroups] = await Promise.all([getProjectById(id), getFormGroupsByProjectId(id)]);

	if (project) {
		// Step 5: Promise array for the built formGroups
		const builtFormGroupPromises = [];

		// Step 6: For each formGroup, get all formInstances by formGroup id
		for (let i = 0; i < formGroups.length; i++) {
			builtFormGroupPromises.push(buildFormGroupForFormGroupList(formGroups[i]));
		}

		// Step 7: Await formInstance promises
		const builtFormGroups = await Promise.all(builtFormGroupPromises);

		// Step 8: FormGroups are done, attach to project, and return. Determine if the db has the project, and if it does, return it. Otherwise, return null
		if (project) {
			const proj: Project = { ...project, formGroups: { items: builtFormGroups } };
			proj.sync = online ? false : true;
			return proj;
		}
	}
	return null;
};

async function _getProject(id: string, online: boolean): Promise<Project | null> {
	try {
		interface QueryResponse {
			getProject: Project;
		}
		if (online) {
			const client = generateClient({ authMode: "userPool" });
			const projectData = (await client.graphql({
				query: getProject,
				variables: {
					id,
				},
				authMode: "userPool",
			})) as unknown as GraphQLResult<QueryResponse>;

			const project = projectData.data.getProject;
			// This if check needs to happen because offline, they might have a project selected that isn't online, then swap to online while in the project, so it tried to retrieve
			if (project) {
				if (project.formGroups.nextToken) {
					const projectBatch = await getBatch({
						id,
						formGroupNextToken: project.formGroups.nextToken,
						prevFormGroupItems: project.formGroups.items,
					});
					project.formGroups.items = projectBatch.prevFormGroupItems;
				}
				const orderedFormGroups = handleformGroupCollation(project.formGroups.items);
				project.formGroups.items = orderedFormGroups;
				project.sync = true;
				return project;
			}
		} else {
			return await getProjectFromIndexedDb(id, online);
		}
	} catch (error: any) {
		// Returning null here makes FormGroupList show the "Oops" screen as if the project were not saved offline, so
		// record which branch failed and the error name (e.g. a Dexie NotFoundError or QuotaExceededError) to tell them apart.
		recordSentry({ error, msg: "ERROR GETTING PROJECTS", additionalData: { id, online, errorName: error?.name } });
	}
	return null;
}
//this function sorts the data to display by last updated by- most recently updated at the top and oldest at the bottom
function sortByDate(formGroups: FormGroup[]): FormGroup[] {
	const sorted = formGroups.sort((a, b) => {
		// valueOf returns it as a number rather than as a date object. While the logic would work with Date objects, ts prefers
		// using numbers to do the arithmetic
		const dateA = new Date(a.updatedAt).valueOf();
		const dateB = new Date(b.updatedAt).valueOf();

		return dateB - dateA;
	});

	return sorted;
}

function handleformGroupCollation(formGroups: FormGroup[]): FormGroup[] {
	const sorted = sortByDate(formGroups);
	return sorted;
}
interface GetBatchParams {
	id: string;
	formGroupNextToken?: string | undefined;
	prevFormGroupItems: FormGroup[];
}

async function getBatch({ id, prevFormGroupItems, formGroupNextToken = undefined }: GetBatchParams): Promise<{ prevFormGroupItems: FormGroup[] }> {
	interface QueryResponse {
		getProject: Project;
	}
	try {
		const client = generateClient({ authMode: "userPool" });
		const projectData = (await client.graphql({
			query: getProject,
			variables: { id, formGroupNextToken } as any,

			authMode: "userPool",
		})) as unknown as GraphQLResult<QueryResponse>;
		const project = projectData.data.getProject;

		if (project.formGroups.nextToken) {
			prevFormGroupItems = prevFormGroupItems.concat(project.formGroups.items);
			return getBatch({ id, formGroupNextToken: project.formGroups.nextToken, prevFormGroupItems });
		} else {
			const retrievedFormGroups = project.formGroups.items;
			prevFormGroupItems = prevFormGroupItems.concat(retrievedFormGroups);
		}
	} catch (error) {
		recordSentry({ error, msg: "Error getting the project:" });
	}
	return { prevFormGroupItems };
}

export default _getProject;
